/** QuickHouse — HUD de la visite virtuelle : viseur central, hauteur/distance du point
 *  visé, cap boussole (nord du projet) et minimap du plan avec la position du joueur.
 *  Superposition DOM au-dessus du canvas 3D (aucun élément three). */
import { useEffect, useRef } from 'react';
import { useProjectStore } from '../store/useProjectStore.js';
import { fmtLen } from '../../shared/geometry.js';

const ACCENT = '#3b9eff';
const CARDINALS = ['N', 'NE', 'E', 'SE', 'S', 'SO', 'O', 'NO'];

/** Minimap : murs de l'étage actif + position/direction du joueur + flèche du nord */
function Minimap({ floor, hud, north }) {
  const ref = useRef(null);
  const W = 150;
  const H = 110;

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = 'rgba(12, 14, 20, 0.85)';
    ctx.fillRect(0, 0, W, H);

    const walls = (floor?.walls ?? []).filter((w) => !w.hidden);
    // Cadrage : murs + joueur, avec une marge
    const pts = walls.flatMap((w) => [w.a, w.b]);
    if (hud?.pos) pts.push(hud.pos);
    if (!pts.length) return;
    let x0 = Infinity; let y0 = Infinity; let x1 = -Infinity; let y1 = -Infinity;
    for (const p of pts) {
      x0 = Math.min(x0, p.x); y0 = Math.min(y0, p.y);
      x1 = Math.max(x1, p.x); y1 = Math.max(y1, p.y);
    }
    const pad = 60; // cm
    const scale = Math.min((W - 16) / Math.max(1, x1 - x0 + pad * 2), (H - 16) / Math.max(1, y1 - y0 + pad * 2));
    const sx = (x) => 8 + (x - x0 + pad) * scale;
    const sy = (y) => 8 + (y - y0 + pad) * scale;

    // Murs
    ctx.strokeStyle = '#8b96ad';
    ctx.lineWidth = 1.4;
    ctx.beginPath();
    for (const w of walls) {
      ctx.moveTo(sx(w.a.x), sy(w.a.y));
      ctx.lineTo(sx(w.b.x), sy(w.b.y));
    }
    ctx.stroke();

    // Joueur : point + cône de vision (azimut plan, depuis −Y, sens horaire)
    if (hud?.pos) {
      const px = sx(hud.pos.x);
      const py = sy(hud.pos.y);
      const az = (hud.azPlan * Math.PI) / 180;
      const dx = Math.sin(az);
      const dy = -Math.cos(az);
      ctx.fillStyle = 'rgba(59, 158, 255, 0.25)';
      ctx.beginPath();
      ctx.moveTo(px, py);
      const spread = 0.5;
      ctx.arc(px, py, 20, Math.atan2(dy, dx) - spread, Math.atan2(dy, dx) + spread);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = ACCENT;
      ctx.beginPath();
      ctx.arc(px, py, 3.5, 0, Math.PI * 2);
      ctx.fill();
    }

    // Flèche du nord (coin haut droit)
    const na = (north * Math.PI) / 180;
    const nx = Math.sin(na);
    const ny = -Math.cos(na);
    const cx = W - 15;
    const cy = 15;
    ctx.strokeStyle = '#e5a73b';
    ctx.lineWidth = 1.6;
    ctx.beginPath();
    ctx.moveTo(cx - nx * 8, cy - ny * 8);
    ctx.lineTo(cx + nx * 8, cy + ny * 8);
    ctx.stroke();
    ctx.fillStyle = '#e5a73b';
    ctx.font = '600 9px "JetBrains Mono", monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('N', cx + nx * 14, cy + ny * 14);
  }, [floor, hud, north]);

  return <canvas ref={ref} width={W} height={H} style={{ display: 'block', borderRadius: 4 }} />;
}

export default function WalkHud() {
  const walkMode = useProjectStore((s) => s.walkMode);
  const measure3d = useProjectStore((s) => s.measure3d);
  const hud = useProjectStore((s) => s.walkHud);
  const project = useProjectStore((s) => s.project);
  const activeFloorId = useProjectStore((s) => s.activeFloorId);
  if (!walkMode || !project) return null;

  const floor = project.floors.find((f) => f.id === activeFloorId) ?? project.floors[0];
  const north = project.settings.north ?? 0;
  const cardinal = hud ? CARDINALS[Math.round(hud.heading / 45) % 8] : '—';

  const box = {
    background: 'rgba(12, 14, 20, 0.85)',
    border: '1px solid #232a3d',
    borderRadius: 4,
    padding: '4px 8px',
    fontFamily: '"JetBrains Mono", monospace',
    fontSize: 11,
    color: '#c7cfdd',
    whiteSpace: 'nowrap',
  };

  return (
    <>
      {/* Viseur central */}
      <div style={{
        position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)',
        pointerEvents: 'none', color: measure3d ? ACCENT : 'rgba(255,255,255,0.75)',
        fontSize: 19, lineHeight: 1, textShadow: '0 0 3px rgba(0,0,0,0.9)',
      }}>
        +
      </div>
      {/* Infos de visée + cap */}
      <div style={{
        position: 'absolute', left: 10, bottom: 10, display: 'flex',
        flexDirection: 'column', gap: 4, pointerEvents: 'none',
      }}>
        <div style={box}>
          Visée : {hud?.aim
            ? `h ${fmtLen(hud.aim.h)} cm — ${hud.aim.d.toFixed(2)} m`
            : '—'}
          {measure3d && <span style={{ color: ACCENT }}> — clic : point de mesure</span>}
        </div>
        <div style={box}>
          Cap : {cardinal} ({hud ? Math.round(hud.heading) : 0}°)
        </div>
      </div>
      {/* Minimap */}
      <div style={{ position: 'absolute', right: 10, bottom: 10, pointerEvents: 'none', border: '1px solid #232a3d', borderRadius: 4 }}>
        <Minimap floor={floor} hud={hud} north={north} />
      </div>
    </>
  );
}
